import * as React from "react";
import { Select, IOption } from "../../atoms/forms/Select";
import { IApplicationState } from "../../../store";
import * as Cities from "../../../store/cities";
import { FetchState } from "../../../entities/FetchState";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IData as CityData } from '../../../store/cities/citiesList';
import { IStoreSelectControls } from "./ICommonInputsControls";

// City interface from the store
type City = CityData;


interface ICity extends IOption {
    city: City;
}

const mapCityToValue = (city: City): ICity => {
    return {
        value: city.id,
        label: `${city.name} (${city.postalCode})`,
        city: city
    }
}

// Props interface
interface IProps {
    selectedCityId?: string;
}

// Actual Component
export type AllProps = IProps & IStoreSelectControls<City>;
export const CitySelect = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tCommon } = useTranslation('common');
    
    // Get Redux state and dispatch
    const citiesState = useSelector((state: IApplicationState) => state.cities.citiesList);
    const dispatch = useDispatch();

    // Internal state for selected city - initialize with preselected city if available
    const [selectedCity, setSelectedCity] = React.useState<ICity | null>(() => {
        // Try to initialize with preselected city if data is already available
        if (citiesState.CitiesList.data && props.selectedCityId) {
            const city = citiesState.CitiesList.data.find(c => c.id === props.selectedCityId);
            if (city) {
                return mapCityToValue(city);
            }
        }
        // If using value from common interface
        else if (props.value) {
            return mapCityToValue(props.value);
        }
        return null;
    });

    // Fetch cities on initial render if not loaded or if the data array is empty
    React.useEffect(() => {
        const shouldFetch = 
            citiesState.CitiesList.state === FetchState.NotFetched || 
            (citiesState.CitiesList.state === FetchState.Success && 
             (!citiesState.CitiesList.data || citiesState.CitiesList.data.length === 0));
             
        if (shouldFetch) {
            dispatch(Cities.ActionCreators.citiesList.FetchData());
        }
    }, [dispatch, citiesState.CitiesList.state, citiesState.CitiesList.data]);

    // Update selected city when data is loaded or selectedCityId prop changes
    React.useEffect(() => {
        if (citiesState.CitiesList.data) {
            if (props.selectedCityId) {
                const city = citiesState.CitiesList.data.find(c => c.id === props.selectedCityId);
                if (city) {
                    const mappedCity = mapCityToValue(city);
                    setSelectedCity(mappedCity);
                } else {
                    setSelectedCity(null);
                }
            } else if (props.value) {
                // Handle value from common interface
                setSelectedCity(mapCityToValue(props.value));
            } else {
                setSelectedCity(null);
            }
        }
    }, [citiesState.CitiesList.data, props.selectedCityId, props.value, citiesState.CitiesList.state]);

    // Map cities to select options
    const cities = React.useMemo(() => {
        return (citiesState.CitiesList.data || [])
            .map(mapCityToValue)
            .sort((a, b) => {
                if (a.label && b.label) {
                    return a.label.localeCompare(b.label);
                }
                // Fallback: compare by id if label is undefined
                return String(a.value).localeCompare(String(b.value));
            });
    }, [citiesState.CitiesList.data, citiesState.CitiesList.state]);

    // Force component re-render when cities list changes state from loading to success
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        if (citiesState.CitiesList.state === FetchState.Success && citiesState.CitiesList.data && citiesState.CitiesList.data.length > 0) {
            setForceUpdateKey(prev => prev + 1);
        }
    }, [citiesState.CitiesList.state, citiesState.CitiesList.data]);

    const updateCity = (city: ICity | null) => {
        setSelectedCity(city);
        
        // Call onChange callback from common interface
        if (props.onChange) {
            props.onChange(city?.city || null, !props.required || !!city);
        }
    }

    const isDisabled = props.disabled || citiesState.CitiesList.state !== FetchState.Success;
    const isLoading = citiesState.CitiesList.state === FetchState.Busy || citiesState.CitiesList.state === FetchState.NotFetched || props.loading;
    const error = props.error || (citiesState.CitiesList.state === FetchState.Error ? [true, tCommon('errorLoadingData')] : undefined);

    return (<Select 
        key={forceUpdateKey}
        label={(props.label || tDatatypes('cityLabel') ||"City") + (citiesState.CitiesList.state !== FetchState.Success ? tCommon('loading') : "")}
        placeholder={props.placeholder || (tDatatypes('cityPlaceholder') || "Select a city...")}
        options={cities}
        value={selectedCity}
        disabled={isDisabled}
        onChange={updateCity}
        loading={isLoading}
        error={error}
        required={props.required}
        prevalidate={props.prevalidate}
        icon={props.icon}
        isUnknownValuesEnabled={props.isUnknownValuesEnabled}
    />)
}
